// The ORG INVITATION ledger — the inviter's pending list, the revoke, and the
// INVITEE's own redemption door (`POST /v1/invitations/accept`).
//
// One module owns whether a token has been SPENT, so a redeem and a re-send
// cannot disagree about it — that is why the accept route lives here rather than
// beside the session doors, and why `redeemedInviteTokens` is a module-level
// Set: a repeat POST for a spent token answers `already_accepted` rather than
// minting a second membership, which is what makes a Back onto a used link read
// the way `routes/Invite.tsx` assumes.
//
// The roster's POST (`org-mocks.ts`) mints and ROTATES rows in `orgInvitations`
// through `liveInvitationFor`; both are exported for exactly that, and this
// module stays the one place the ledger is declared.

import { ApiError } from './api';
import { at, me } from './mock-session';

// The org's invitations (arc 101 slice 01 + task #19). Mutable for the same
// reason the roster below is, and STATEFUL for a second: `<PendingInvitations/>`
// reads `GET /v1/org/members/invitations` and revokes through
// `DELETE …/invitations/:id`, so a stateless echo would show a minted row that a
// revoke could never remove — the panel's whole loop, unexercisable in a dev
// story or an e2e run.
//
// It is also what makes `reissued` honest: a second invite for a live address
// ROTATES that invitation rather than minting a second one, and the notice says
// so. Keyed by email for exactly that, since that is the key the route rotates on.
export type MockInvitation = {
  id: string;
  kind: 'org_member';
  account_id: string | null;
  role: string;
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  invited_by_user_id: string | null;
  expires_at_iso: string;
  created_at_iso: string;
  accepted_at_iso: string | null;
  revoked_at_iso: string | null;
};
export const orgInvitations: { data: MockInvitation[]; next_cursor: null } = {
  next_cursor: null,
  data: [],
};

/** The live (`pending`) invitation for an address, if this session minted one. */
export const liveInvitationFor = (email: string): MockInvitation | undefined =>
  orgInvitations.data.find((i) => i.email === email && i.status === 'pending');

// Tokens `/v1/invitations/accept` has already redeemed (e2e story #21) — the
// invitee-side mirror of `orgInvitedEmails` above, keyed by the invitation
// token rather than the invited address.
const redeemedInviteTokens = new Set<string>();

/** Identity, kept so the branch bodies below read as they did inside the ladder. */
const reply = (value: unknown): Promise<unknown> => Promise.resolve(value);

/** Answer `/v1/invitations/accept`, or `null` for "not my route". */
export function invitationAcceptMock(
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  // Invitation redemption (arc 101 slice 01 step 3; e2e story #21): the
  // invitee's side of `<Invite/>`. Keyed by TOKEN, same as the real route — a
  // repeat POST for a token already redeemed answers `already_accepted` rather
  // than minting a second membership, so the e2e story that lands back on this
  // URL after a completed sign-in never redeems twice and a Back onto a spent
  // link reads the way `routes/Invite.tsx`'s `replace` navigation assumes it
  // will. The default account (`me`) is already a `developerz-ai` member, so
  // any token not special-cased below resumes that org's onboarding funnel —
  // which is the story `e2e/onboarding.spec.ts` drives.
  if (path === '/v1/invitations/accept' && method === 'POST') {
    const body = init?.body ? (JSON.parse(String(init.body)) as { token?: string }) : {};
    const token = body.token ?? '';
    // The API refuses a body with no token at the edge (`InvitationTokenSchema`
    // is `min(1)`), so a mock that redeemed `''` would let a story pass that the
    // real route 400s — and would file the empty string in `redeemedInviteTokens`,
    // making the SECOND such call answer `already_accepted` for an invitation
    // nobody ever held.
    if (typeof body.token !== 'string' || token.trim() === '') {
      return Promise.reject(
        new ApiError(400, 'invalid accept body', undefined, 'token is required'),
      );
    }
    if (token === 'dev_inv_e2e_expired') {
      return Promise.reject(
        new ApiError(410, 'this invitation expired', undefined, undefined, {
          reason: 'expired',
          expires_at: at('2026-07-01'),
        }),
      );
    }
    if (redeemedInviteTokens.has(token)) {
      return reply({ already_accepted: true, accepted_at: at('2026-07-20'), by_self: true });
    }
    redeemedInviteTokens.add(token);
    return reply({ accepted: true, account_id: me.id, role: 'member' });
  }
  return null;
}

/** Answer `/v1/org/members/invitations*`, or `null` for "not my route". */
export function orgInvitationsRoute(path: string, method: string): Promise<unknown> | null {
  // BEFORE the `:userId` route below, exactly as `createOrgMemberRoutes`
  // registers them: Hono matches in registration order and `/v1/org/members/:userId`
  // matches the concrete path `/v1/org/members/invitations`. Behind the generic
  // handler this GET fell through to `unhandled`, so the pending-invitations
  // panel could not load at all under mocks.
  if (path === '/v1/org/members/invitations') return reply(orgInvitations);
  if (path.startsWith('/v1/org/members/invitations/') && method === 'DELETE') {
    const id = decodeURIComponent(path.replace('/v1/org/members/invitations/', ''));
    const row = orgInvitations.data.find((i) => i.id === id);
    // Idempotent, as the route is: an already-revoked row settles the same way.
    // An accepted one is the 409 — you cannot withdraw someone who joined.
    if (row?.status === 'accepted') {
      return Promise.reject(
        new ApiError(409, 'already accepted', undefined, 'remove the member instead'),
      );
    }
    if (row === undefined) return Promise.reject(new ApiError(404, 'no such invitation'));
    row.status = 'revoked';
    row.revoked_at_iso = at('2026-07-22');
    orgInvitations.data = [...orgInvitations.data];
    return reply({});
  }
  return null;
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_REDEEMEDINVITETOKENS: readonly string[] = [...redeemedInviteTokens];

export function resetOrgInvitationMocks(): void {
  redeemedInviteTokens.clear();
  for (const entry of PRISTINE_REDEEMEDINVITETOKENS) redeemedInviteTokens.add(entry);
}
